// Export a dashboard's slides to a multi-page PDF, one slide per page.
//
// html2canvas + jsPDF are heavy, so they're dynamically imported here — the
// bundle only pays for them when the user actually exports. Slides are captured
// from the live viewer: the caller flips to each slide (showSlide) and we snap
// the on-screen node once it has settled (charts need a beat to (re)draw).

/**
 * @param {Object}   opts
 * @param {number}   opts.slideCount
 * @param {(i:number)=>void} opts.showSlide   switch the viewer to slide i
 * @param {()=>HTMLElement|null} opts.getNode  current slide's DOM node to snap
 * @param {string}   opts.fileName
 * @param {number}   [opts.waitMs=450]         settle time before each capture
 */
export const exportSlidesToPdf = async ({
  slideCount,
  showSlide,
  getNode,
  fileName,
  waitMs = 450,
}) => {
  const [{ jsPDF }, html2canvasMod] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);
  const html2canvas = html2canvasMod.default;

  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < slideCount; i++) {
    showSlide(i);
    // Let React commit the slide change and ECharts finish drawing.
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, waitMs));

    const node = getNode();
    if (!node) continue;

    // eslint-disable-next-line no-await-in-loop
    const canvas = await html2canvas(node, {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
      useCORS: true,
    });
    const img = canvas.toDataURL("image/png");

    // Fit the capture into the page, centered, preserving aspect ratio.
    const ratio = Math.min(pw / canvas.width, ph / canvas.height);
    const w = canvas.width * ratio;
    const h = canvas.height * ratio;
    if (i > 0) pdf.addPage();
    pdf.addImage(img, "PNG", (pw - w) / 2, (ph - h) / 2, w, h);
  }

  pdf.save(fileName);
};
